import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import ScrollIndicator from './ScrollIndicator';
import { useAnalytics } from '../utils/analytics/useAnalytics';

const sections = [
  { id: 'home-hero-section', label: 'Welcome' },
  { id: 'home-facts-section', label: 'Our Mission' },
  { id: 'home-expertise-section', label: 'Our Expertise' },
  { id: 'home-action-section', label: 'Get Started' }
];

const ANIMATION_SEQUENCE = [
  { stage: 1, action: 'showArt', delay: 300 },
  { stage: 2, action: 'showX', delay: 1200 },
  { stage: 3, action: 'showTech', delay: 2100 },
  { stage: 3, action: 'hideAll', delay: 3500 },
  { stage: 3, action: 'reduceHeight', delay: 4000 },
  { stage: 3, action: 'showNewContent', delay: 4800 }
];

function Home() {
  const [animationState, setAnimationState] = useState({
    currentStage: 0,
    showArt: false,
    showX: false,
    showTech: false,
    showNewContent: false,
    reduceHeight: false
  });

  const { track } = useAnalytics();
  const navigate = useNavigate();

  useEffect(() => {
    track('page_view', {
      category: 'Home',
      label: 'Home Page'
    });
  }, [track]);

  useEffect(() => {
    const handleScroll = () => {
      track('scroll', {
        category: 'Home',
        label: 'Home Page Scroll'
      });
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent % 25 === 0) {
        track('scroll', {
          category: 'Home',
          label: `Home Page Scroll ${scrollPercent}%`
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [track]);

  useEffect(() => {
    let hasSeenAnimation = false;
    const timeouts = [];
    
    try {
      hasSeenAnimation = sessionStorage.getItem('homeAnimation') === 'true';
    } catch (e) {
      console.error('Failed to access sessionStorage:', e);
    }
    
    const handleBeforeUnload = () => {
      try {
        sessionStorage.removeItem('homeAnimation');
      } catch (e) {
        console.error('Failed to remove from sessionStorage:', e);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    if (hasSeenAnimation) {
      setAnimationState(state => ({
        ...state,
        currentStage: 3,
        showNewContent: true,
        reduceHeight: true
      }));
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }

    // Play animation sequence
    ANIMATION_SEQUENCE.forEach(({ stage, action, delay }) => {
      timeouts.push(setTimeout(() => {
        setAnimationState(state => {
          const newState = { ...state, currentStage: stage };
          
          switch (action) {
            case 'showArt':
              return { ...newState, showArt: true };
            case 'showX':
              return { ...newState, showX: true };
            case 'showTech':
              return { ...newState, showTech: true };
            case 'hideAll':
              return { ...newState, showArt: false, showX: false, showTech: false };
            case 'reduceHeight':
              return { ...newState, reduceHeight: true };
            case 'showNewContent':
              try {
                sessionStorage.setItem('homeAnimation', 'true');
              } catch (e) {
                console.error('Failed to set sessionStorage:', e);
              }
              return { ...newState, showNewContent: true };
            default:
              return state;
          }
        });
      }, delay));
    });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      timeouts.forEach(t => clearTimeout(t));
    };
  }, []);

  const { currentStage, showArt, showX, showTech, showNewContent, reduceHeight } = animationState;

  const handleContactClick = () => {
    track('button_click', {
      category: 'Home',
      label: 'Contact Us'
    });
    navigate('/contact-us');
  };

  return (
    <div className="home">
      <ScrollIndicator sections={sections} />
      {/* Hero Section */}
      <section className={`home-section home-hero-section stage-${currentStage} ${reduceHeight ? 'reduced' : ''}`} id='home-hero-section'>
        <div className="home-hero-content">
          {!showNewContent ? (
            <div className="home-hero-text">
              <span className={`art-text ${showArt ? 'show' : ''}`}>TECH</span>
              <span className={`x-text ${showX ? 'show' : ''}`}>×</span>
              <span className={`tech-text ${showTech ? 'show' : ''}`}>ALL</span>
            </div>
          ) : (
            <div className="home-hero-new-content">
              <div className="home-hero-text-wrapper">
                <h1>
                  We are<span className="home-comma">,</span>
                  <br />
                  TearFace<span className="home-dot">.</span>
                </h1>
              </div>
              <div className="home-hero-logo">
                <img src="/images/TearFace.png" alt="TearFace Logo" />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Facts Section */}
      <section className="home-section home-facts-section" id='home-facts-section'>
        <div className="home-facts-content">
          <h2>Making Tech Useful, <br/> For Everyone.</h2>
          <p>
          Technology should feel natural and effortless, empowering users. <br/>
          When done right, it disappears into the background, letting people focus on what truly matters.
          </p>
          
          {/* Customer Trust Section */}
          <div className="home-customer-trust">
            <div className="trust-divider">
              <span className="trust-line"></span>
              <span className="trust-text">Trusted By</span>
              <span className="trust-line"></span>
            </div>
            <div className="home-customers-showcase">
              <a href="https://www.samos.com" target="_blank" rel="noopener noreferrer" className="customer-logo-card">
                <div className="logo-container">
                  <img src="/images/customer_logo1.png" alt="Samos" />
                </div>
                <div className="logo-glow"></div>
              </a>
              <a href="https://robotxworkshops.tech" target="_blank" rel="noopener noreferrer" className="customer-logo-card">
                <div className="logo-container">
                  <img src="/images/customer_logo2.png" alt="RobotX Workshops" />
                </div>
                <div className="logo-glow"></div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="home-section home-expertise-section" id='home-expertise-section'>
        <h3>
          Our<span className="comma">,</span> Expertise<span className="dot">.</span>
        </h3>
        <div className="home-expertise-grid">
          <div className="expertise-card">
            <img src="/images/home-expertise1.jpg" alt="Brand & Product Design" />
            <h4>Brand & Product Design</h4>
            <p>Identity, UX/UI, Systems</p>
          </div>
          <div className="expertise-card">
            <img src="/images/home-expertise2.jpg" alt="Engineering & Development" />
            <h4>Engineering & Development</h4>
            <p>Web, Mobile, Backend</p>
          </div>
          <div className="expertise-card">
            <img src="/images/home-expertise3.jpg" alt="Digital Strategy & Research" />
            <h4>Digital Strategy & Research</h4>
            <p>Human-centered insights</p>
          </div>
          <div className="expertise-card">
            <img src="/images/home-expertise4.jpg" alt="Creative Technology" />
            <h4>Creative Technology</h4>
            <p>Prototypes, animations, immersive UX</p>
          </div>
        </div>
      </section>

      {/* Action Section */}
      <section className="home-section home-action-section" id='home-action-section'>
        <div className="home-action-content">
          <h3>Untangle<span className="comma">,</span> Your Tech<span className="dot">.</span></h3>
          <button className="home-action-button" onClick={handleContactClick}>
            Let's Go!
          </button>
        </div>
      </section>
    </div>
  );
}

export default Home;